<template>
  <div id="shell">
    <template v-if="navType === 'DV'">
      <DvNav
        v-show="!hideMainNav"
        :app-constant-data="appConstantData"
        :show-side-nav-toggler="showSideNavToggler"
        :search-type="searchType"
        :show-search-options="showSearchOptions"
        :show-create-player="showCreatePlayer"
        :show-create-school="showCreateSchool"
        @createPlayer="(searchType) => $emit('createPlayer', searchType)"
        @createSchool="(searchType) => $emit('createSchool', searchType)"
        @togglesidenav="isSideNavCollpased = !isSideNavCollpased"
      />
    </template>

    <div
      id="container"
      class="shell-container"
    >
      <template v-if="showLoader && isLoading">
        <LoadingSpinner position="absolute" />
      </template>

      <div
        v-show="!isSideNavCollpased"
        id="sidenav"
        class="sidenav"
      >
        <slot name="sidenav">
          <template v-if="routes.length && appConstantData">
            <section class="sidenav__content">
              <slot name="title">
                <template v-if="appTitle">
                  <template v-if="appTitleLogoLink">
                    <a :href="appTitleLogoLink">
                      <div class="sidenav__content--title">
                        <template v-if="appTitleLogoSrc">
                          <div class="sidenav__content--title-logo">
                            <img
                              title="App Logo"
                              :src="appTitleLogoSrc"
                            />
                          </div>

                          <span>{{ appTitle }}</span>
                        </template>
                      </div>
                    </a>
                  </template>

                  <template v-else>
                    <div class="sidenav__content--title">
                      <template v-if="appTitleLogoSrc">
                        <div class="sidenav__content--title-logo">
                          <img
                            title="App Logo"
                            :src="appTitleLogoSrc"
                          />
                        </div>

                        <span>{{ appTitle }}</span>
                      </template>
                    </div>
                  </template>
                </template>
              </slot>

              <slot name="links">
                <template v-if="expandedRoutes">
                  <ul class="sidenav__content--links">
                    <template v-for="(route, i) in expandedRoutes">
                      <li
                        :key="`${route.label}-${i}`"
                        class="sidenav__content--link"
                        :class="{
                          'child-link': route.isChildLink,
                          active: route.isActive,
                        }"
                        @click="setRouteActive(route)"
                      >
                        <template v-if="route.iconPath">
                          <svg
                            version="1.1"
                            class="icons"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            x="0px"
                            y="0px"
                            viewBox="0 0 20 20"
                            enable-background="new 0 0 20 20"
                            xml:space="preserve"
                          >
                            <path :d="route.iconPath" />
                          </svg>
                        </template>

                        <template v-if="route.customLink">
                          <a @click="route.customLink">
                            {{ route.label }}
                          </a>
                        </template>
                        <template v-else>
                          <a
                            :href="route.link"
                            :target="route.isExternal ? '_blank' : '_self'"
                          >
                            {{ route.label }}
                          </a>
                        </template>

                        <template v-if="route.indicator !== undefined && route.indicator !== null">
                          <div class="indicator">
                            {{ route.indicator || 0 }}
                          </div>
                        </template>
                      </li>
                    </template>
                  </ul>
                </template>
              </slot>
            </section>
          </template>
        </slot>
      </div>

      <div id="page-content">
        <template v-if="appConstantData">
          <slot
            name="content"
            :app-constant-data="appConstantData"
          />
        </template>

        <template v-else-if="errorLoadingAppConstants">
          There was an error loading app constants. Please try again.
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '@baseball-systems/LoadingSpinner';

import DvNav from 'library/components/nav/DvNav.vue';

import { notificationsMixin } from 'library/mixins/notifications';
import { httpMixin } from 'library/mixins/http';
import { setDatadogUser } from 'library/helpers/tracing';

const SEARCH_TYPES = ['dv', 'pd', 'pro', 'amateur', 'intl'];

export default {
  name: 'DVShell',
  components: {
    DvNav,
    LoadingSpinner,
  },
  mixins: [notificationsMixin, httpMixin],
  props: {
    /**
     * [
     * 	{
     * 		label: 'Properties',
     *      children: [
     *         {
     *             label: 'Bio',
     *             link: '/properties/bio'
     *         }
     *      ]
     *  }
     * ]
     */
    routes: {
      type: Array,
      default: () => [],
      validate: (vals) => vals?.every((val) => val.label && (val.link || val.customLink)),
    },
    navType: {
      type: String,
      default: 'DV',
    },
    searchType: {
      type: String,
      default: 'dv',
      validate: (val) => SEARCH_TYPES.includes(val),
    },
    hideMainNav: {
      type: Boolean,
      default: false,
    },
    collapseSideNav: {
      type: Boolean,
      default: false,
    },
    showSideNavToggler: {
      type: Boolean,
      default: true,
    },
    appTitle: {
      type: String,
      default: '',
    },
    appTitleLogoSrc: {
      type: String,
      default: '',
    },
    appTitleLogoLink: {
      type: String,
      default: '',
    },
    openLinkInNewTab: {
      type: Boolean,
      default: false,
    },
    showLoader: {
      type: Boolean,
      default: false,
    },
    showSearchOptions: {
      type: Boolean,
      default: false,
    },
    showCreatePlayer: {
      type: Boolean,
      default: false,
    },
    showCreateSchool: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mappedRoutes: this.mapRoutes(this.routes),

      isSideNavCollpased: this.collapseSideNav,
      appConstantData: null,
      errorLoadingAppConstants: false,

      isLoading: false,
    };
  },
  computed: {
    expandedRoutes() {
      return this.mappedRoutes.reduce((prev, route) => {
        prev.push(route);

        if (Array.isArray(route.children)) {
          const isAnyChildActive = route.children.some((child) => child.isActive);
          if (route.isActive || isAnyChildActive) {
            prev.push(...route.children);
          }
        }

        return prev;
      }, []);
    },
  },
  watch: {
    routes: {
      handler(routes) {
        this.mappedRoutes = this.mapRoutes(routes);
      },
      deep: true,
    },
    collapseSideNav(collapsed) {
      this.isSideNavCollpased = collapsed;
    },
  },
  created() {
    this.isLoading = true;

    this.request('/dvsvc/AppConstants').then((resp) => {
      this.isLoading = false;

      if (resp.IsError) {
        this.errorLoadingAppConstants = true;
        this.notifyDanger('Error: appconstants service');
        return this.$emit('error:appConstants', resp);
      }

      if (resp?.UserGuid) {
        localStorage.setItem('dv-user-guid', resp.UserGuid);
      }

      if (resp?.UserGuid) {
        localStorage.setItem('dv-user-guid', resp.UserGuid);
      }

      // Update Datadog user information with AppConstants data
      if (resp?.UserGuid) {
        this.updateDatadogUser(resp);
      }

      if (resp && resp.Roles && resp.Roles.length === 1 && resp.Roles[0] === 'Player') {
        window.location = '/doby';
        return;
      } else {
        this.appConstantData = resp;
        window.appConstantData = resp;

        this.$emit('retrieve:appConstants', resp);
      }
    });
  },
  methods: {
    mapRoutes(routes) {
      const setRouteConfig = (route, isChildLink = false) => {
        const isRouteExternal = (route.isExternal || this.openLinkInNewTab) && !isChildLink;

        return {
          ...route,
          link: !isChildLink ? route.link : `${route.link}#${route.hash}`,
          isExternal: isRouteExternal,
          isActive: this.isRouteLinkActive(route, isChildLink),
          isChildLink,
        };
      };

      return routes.reduce((prev, route) => {
        const mappedBaseRoute = setRouteConfig(route);
        if (Array.isArray(route.children)) {
          mappedBaseRoute.children = mappedBaseRoute.children.map((child) => setRouteConfig(child, true));
        }

        prev.push(mappedBaseRoute);

        return prev;
      }, []);
    },
    isRouteLinkActive(route, isChild = false) {
      const { pathname, search, hash } = window.location;

      const parsedHash = hash?.startsWith('#') && hash.slice(1);
      const currentPath = `${pathname}${search ?? ''}`;
      return isChild
        ? route.link === currentPath && route.hash === parsedHash
        : route.link === currentPath &&
            (route?.children ? !route.children.some((child) => child.hash === parsedHash) : true);
    },
    setRouteActive(route) {
      // Don't need to set active if external link clicked.
      if (route.isExternal) return;

      this.expandedRoutes.forEach((r) => {
        r.isActive = r.link === route.link;
      });
    },
    updateDatadogUser(appConstantData) {
      // Update Datadog with user information from AppConstants
      const userId = appConstantData.UserGuid;
      const userInfo = {
        UserName: appConstantData.UserName,
        ScoutName: appConstantData.ScoutName,
        ImAProxyLogin: appConstantData.ImAProxyLogin,
      };
      
      setDatadogUser(userId, userInfo);
    },
  },
};
</script>

<style scoped lang="scss">
#shell {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
  min-height: 100%;
  background: white;

  #mainNav {
    :deep(.result) {
      text-wrap: nowrap;
    }
  }

  #container {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    height: calc(100% - 44px);

    .sidenav {
      max-width: 175px;
      min-width: 175px;
      background: $scouting-blue;

      overflow-y: auto;

      -ms-overflow-style: none;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      &__content {
        &--title {
          font-weight: bold;
          font-size: 20px;
          text-align: center;
          list-style-type: none;
          border-top: 1px solid $scouting-gray;
          border-bottom: 1px solid #444;
          padding: 5px;
          cursor: pointer;
          line-height: 16px;
          color: white;

          &-logo {
            display: inline-block;
            vertical-align: middle;

            img {
              width: 30px;
            }
          }
        }

        &--link {
          font-size: 12px;
          list-style-type: none;
          border-top: 1px solid $scouting-gray;
          border-bottom: 1px solid #222;
          background: #344561;
          cursor: pointer;
          line-height: 16px;

          display: -webkit-box;
          display: -ms-flexbox;
          display: flex;
          position: relative;

          -webkit-box-align: center;
          -ms-flex-align: center;
          align-items: center;

          &.active {
            background-color: rgba(white, 0.4);
          }

          &.child-link {
            transition: height 0.3s;
            line-height: 16px;
            color: #fff;
            list-style-type: none;
            font-size: 12px;

            &:not(&.active) {
              background-color: #222d40;
            }
          }

          a {
            color: white;
            width: 100%;
            height: 100%;
            padding: 5px;
          }

          .indicator {
            width: fit-content;
            background-color: #fff;
            color: #000;
            padding: 2px 5px;
            position: absolute;
            right: 5px;
          }
        }
      }
    }

    #page-content {
      position: relative;
      flex-grow: 1;
      max-height: 100%;
      overflow-y: auto;
      overflow-x: auto;
    }
  }
}
</style>
